import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { FaFileExcel } from "react-icons/fa";
import { FcClock } from "react-icons/fc";
import { HiBadgeCheck } from "react-icons/hi";
import { IoReload } from "react-icons/io5";
import { RiCloseCircleFill } from "react-icons/ri";
import { useHistory } from "react-router-dom";
import { panelTypesDefault } from "../../../@types/panelTypes";
import { GroupInput } from "../../../components/Form/GroupInput";
import { Input } from "../../../components/Form/Input";
import { Select } from "../../../components/Form/Select";
import { PanelAndDetailAndCreateHeader } from "../../../components/panel/PanelAndDetailAndCreateHeader";
import { PanelFilter } from "../../../components/panel/PanelFilter";
import { PanelLayout } from "../../../components/panel/PanelLayout";
import { Pagination } from "../../../components/panel/table/Pagination";
import {
  FieldProps,
  TableContent,
} from "../../../components/panel/table/TableContent";
import { TableHeader } from "../../../components/panel/table/TableHeader";
import {
  OrderConfirm,
  OrderConfirmFilters,
  getOrderConfirmes,
  useOrderConfirmes,
  useOrderFilters,
} from "../../../hook/queries/useOrdersCofirmes";
import { setQueryParams, useQueryParams } from "../../../hook/useQueryParams";
import * as sellerLocalStorage from "../../../service/localStorage/seller";
import { generateXlsx } from "../../../utils/generateXlsx";
import { objectToForEach } from "../../../utils/objectToForEach";
import { setFormikValues } from "../../../utils/setFormikValues";

interface PanelOrderConfirmProps extends panelTypesDefault {}

export const columnsOrderConfirm: FieldProps<OrderConfirm>[] = [
  {
    name: "confirm",
    field: "CONFIRMADO",
    viewList: true,
    render({ confirmed }) {
      if (confirmed === undefined || confirmed === null)
        return <FcClock color="#417DAB" size={26} />;

      return true ? (
        <HiBadgeCheck color="#21b543" size={26} />
      ) : (
        <RiCloseCircleFill color="#d21e26" size={26} />
      );
    },
  },
  {
    name: "orderId",
    field: "CÓDIGO",
    viewList: true,
    tdStyles() {
      return { width: 140, whiteSpace: "nowrap" };
    },
  },
  {
    name: "brand",
    field: "MARCA",
    viewList: true,
    render({ order }) {
      return order?.brand?.name;
    },
  },
  {
    name: "seller",
    field: "REPRESENTANTE",
    viewList: true,
    render({ seller }) {
      return seller?.abbreviation;
    },
  },
  {
    name: "client",
    field: "CLIENTE",
    viewList: true,
    render({ client }) {
      return client?.company_name ?? "-";
    },
  },
  {
    name: "entryDate",
    field: "DATA DO PEDIDO",
    viewList: true,
    render({ order }) {
      return moment(order?.entryDate).format("DD/MM/YYYY");
    },
  },
  {
    name: "totalValue",
    field: "VALOR TOTAL",
    viewList: true,
    render({ order }) {
      return Number(order?.totalInvoiceValue).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
  },

  { name: "codSeller", field: "CÓD. VENDEDOR" },
  { name: "codClient", field: "CÓD. CLIENTE" },
  { name: "cnpj", field: "CNPJ" },
  { name: "company_name", field: "RAZÃO SOCIAL" },
  {
    name: "orderPeriod",
    field: "DATA DO PEDIDO",
  },
  {
    name: "orderPresetDate",
    field: "DATA PREDEFINIDA",
  },
  {
    name: "orderSpecificDate",
    field: "DATA ESPECÍFICA",
  },
  {
    name: "orderStartDate",
    field: "DATA INICIAL",
  },
  {
    name: "orderEndDate",
    field: "DATA FINAL",
  },
  {
    name: "confirmed",
    field: "CONFIRMAÇÃO",
  },
  {
    name: "cod",
    field: "CÓDIGO",
    viewList: false,
  },
];

const PanelOrderConfirm: React.FC<PanelOrderConfirmProps> = ({
  admin,
  client,
}) => {
  const history = useHistory();
  const query = useQueryParams();
  const [sellerStorage] = useState(
    () =>
      sellerLocalStorage.getUser() as sellerLocalStorage.SellerProps | undefined
  );

  const [page, setPage] = useState<number>(() => {
    const pageQuery = query.get("page");

    if (!isNaN(Number(pageQuery)) && Number(pageQuery) > 0)
      return Number(pageQuery);

    return 1;
  });
  const [filters, setFilters] = useState<OrderConfirmFilters>(() => {
    let filtersValueQueryParams = {};
    query.forEach((value, key) => {
      const findOne = columnsOrderConfirm.find((f) => f.name === key);

      if (findOne) {
        filtersValueQueryParams = {
          ...filtersValueQueryParams,
          [key]: value,
        };
      }
    });

    return filtersValueQueryParams as any;
  });
  const [viewFilter, setViewFilter] = useState(false);
  const { data, isLoading, isFetching, refetch } = useOrderConfirmes(
    page,
    filters
  );
  const getOrderFilters = useOrderFilters({
    ...filters,
  });

  const formikFilter = useFormik({
    initialValues: {
      cod: undefined,
      codClient: undefined,
      cnpj: undefined,
      codSeller: undefined,
      brand: undefined,
      orderPeriod: undefined,
      orderPresetDate: undefined,
      orderSpecificDate: undefined,
      orderStartDate: undefined,
      orderEndDate: undefined,
    },
    onSubmit: (data) => {
      handleFilter(data as any);
    },
  });

  useEffect(() => {
    if (page > 0) {
      setQueryParams({
        data: {
          field: "page",
          value: String(page),
        },
        type: "set",
        history,
      });
    }
  }, [page, history]);

  useEffect(() => {
    let filtersValueQueryParams = {};
    query.forEach((value, key) => {
      const findOne = columnsOrderConfirm.find((f) => f.name === key);

      if (findOne) {
        filtersValueQueryParams = {
          ...filtersValueQueryParams,
          [key]: value,
        };
      }
    });

    setFormikValues(filtersValueQueryParams, formikFilter);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    objectToForEach(filters, (key, value) => {
      if (value) {
        setQueryParams({
          data: {
            field: String(key),
            value: String(value),
          },
          type: "set",
          history,
        });
      } else {
        setQueryParams({
          data: {
            field: String(key),
          },
          type: "delete",
          history,
        });
      }
    });

    setFormikValues(filters, formikFilter);
    // eslint-disable-next-line
  }, [filters]);

  function handleFilter(data: OrderConfirmFilters) {
    setFilters(data);
    setPage(1);
    setViewFilter(false);
  }

  async function handleExport() {
    const getData = await getOrderConfirmes(1, filters, 99999);

    generateXlsx({
      data: getData.orderConfirmes.map((order) => ({
        CONFIRMADO:
          order.confirmed === undefined || order.confirmed === null
            ? "PENDENTE"
            : order.confirmed
            ? "SIM"
            : "NÃO",
        CÓDIGO: order.orderId,
        MARCA: order.order.brand?.name,
        "CÓD. REPRESENTANTE": order?.seller?.cod,
        REPRESENTANTE: order?.seller?.abbreviation,
        "TIPO REPRESENTANTE": order?.seller?.typeSeller,
        "CÓD. REPRESENTANTE PRINCIPAL": order?.seller?.sellerMain ?? "-",
        "CÓD. GNV": order?.seller?.cod_manager,
        "CÓD. GRV": order?.seller?.cod_supervisor ?? "-",
        CLIENTE: order.client?.company_name ?? "-",
        "DATA DO PEDIDO": order.order
          ? moment(order.order?.entryDate).format("DD/MM/YYYY")
          : "",
        "VALOR TOTAL": Number(order?.order?.totalInvoiceValue),
      })),
      filename: "confirmação de pedido",
    });
  }

  const isManagerOrSupervisor =
    !!sellerStorage?.is_manager || !!sellerStorage?.is_supervisor;

  return (
    <PanelLayout>
      <PanelAndDetailAndCreateHeader title="Lista de confirmação de pedido" />

      <PanelFilter
        filters={filters}
        setFilters={setFilters}
        columns={columnsOrderConfirm}
        onSubmit={formikFilter.handleSubmit}
        onResetForm={formikFilter.resetForm}
        setViewFilter={setViewFilter}
        viewFilter={viewFilter}
        normalizedFilters={(value, label) => {
          switch (label) {
            case "brand":
              return getOrderFilters.data?.brands?.find(
                (f) => Number(f.id) === Number(value)
              )?.name;

            case "orderPeriod":
              if (Number(value) === 1) {
                return "Predefinida";
              }
              if (Number(value) === 2) {
                return "Específica";
              }
              if (Number(value) === 3) {
                return "Intevalo";
              }
              break;
            case "orderPresetDate":
              if (Number(value) === 0) {
                return "Hoje";
              }
              if (Number(value) === 2) {
                return "Ontem";
              }
              if (Number(value) === 7) {
                return "Últimos 7 dias";
              }
              if (Number(value) === 15) {
                return "Últimos 15 dias";
              }
              if (Number(value) === 30) {
                return "Últimos 30 dias";
              }
              break;

            default:
              return value;
          }
        }}
      >
        <GroupInput>
          <Input
            label="CÓDIGO DO PEDIDO"
            {...formikFilter.getFieldProps("cod")}
          />
        </GroupInput>

        {!client && (
          <GroupInput>
            <Input
              label="CÓDIGO CLIENTE"
              {...formikFilter.getFieldProps("codClient")}
            />
            <Input label="CNPJ" {...formikFilter.getFieldProps("cnpj")} />
          </GroupInput>
        )}

        <GroupInput>
          <Select
            label="CÓD. VENDEDOR"
            data={
              getOrderFilters?.data?.sellers?.map((seller) => ({
                name: `${String(seller?.id)} ${String(seller?.name)}`,
                value: String(seller?.id),
              })) ?? []
            }
            {...formikFilter.getFieldProps("codSeller")}
          />

          <Select
            label="MARCA"
            data={
              getOrderFilters?.data?.brands?.map((brand) => ({
                name: String(brand.name),
                value: String(brand.id),
              })) ?? []
            }
            {...formikFilter.getFieldProps("brand")}
          />

          <Select
            label="CONFIRMAÇÃO"
            data={[
              { name: "CONFIRMADO", value: "CONFIRMADO" },
              { name: "PENDENTE", value: "PENDENTE" },
            ]}
            {...formikFilter.getFieldProps("confirmed")}
          />
        </GroupInput>

        <Select
          label="DATA DO PEDIDO"
          data={[
            { name: "Predefinida", value: 1 },
            { name: "Específica", value: 2 },
            { name: "Intervalo", value: 3 },
          ]}
          {...formikFilter.getFieldProps("orderPeriod")}
        />

        {Number(formikFilter.values.orderPeriod) === 1 && (
          <Select
            label="DATA DO PEDIDO"
            data={[
              { name: "Hoje", value: 0 },
              { name: "Ontem", value: 1 },
              { name: "Últimos 7 dias", value: 7 },
              { name: "Últimos 15 dias", value: 15 },
              { name: "Últimos 30 dias", value: 30 },
            ]}
            {...formikFilter.getFieldProps("orderPresetDate")}
            error={
              formikFilter.touched.orderPresetDate &&
              formikFilter.errors.orderPresetDate
                ? formikFilter.errors.orderPresetDate
                : undefined
            }
          />
        )}
        {Number(formikFilter.values.orderPeriod) === 2 && (
          <Input
            type="date"
            label="DATA DO PEDIDO"
            {...formikFilter.getFieldProps("orderSpecificDate")}
            error={
              formikFilter.touched.orderSpecificDate &&
              formikFilter.errors.orderSpecificDate
                ? formikFilter.errors.orderSpecificDate
                : undefined
            }
          />
        )}

        {Number(formikFilter.values.orderPeriod) === 3 && (
          <GroupInput>
            <Input
              type="date"
              label="DATA INICIAL"
              {...formikFilter.getFieldProps("orderStartDate")}
              error={
                formikFilter.touched.orderStartDate &&
                formikFilter.errors.orderStartDate
                  ? formikFilter.errors.orderStartDate
                  : undefined
              }
            />
            <Input
              type="date"
              label="DATA FINAL"
              {...formikFilter.getFieldProps("orderEndDate")}
              error={
                formikFilter.touched.orderEndDate &&
                formikFilter.errors.orderEndDate
                  ? formikFilter.errors.orderEndDate
                  : undefined
              }
            />
          </GroupInput>
        )}
      </PanelFilter>

      <TableHeader title="PEDIDOS" isFetching={isFetching && !isLoading}>
        {(admin || isManagerOrSupervisor) && (
          <button style={{ background: "#2d792f" }} onClick={handleExport}>
            <FaFileExcel size={19} />
            <span>EXPORTAR</span>
          </button>
        )}

        <button onClick={() => refetch()}>
          <IoReload size={19} />
          <span>RECARREGAR</span>
        </button>
      </TableHeader>

      <TableContent<OrderConfirm>
        data={data?.orderConfirmes}
        columns={columnsOrderConfirm.filter((f) => f.viewList)}
        isLoading={isLoading}
        onClickRow={(e) =>
          history.push(
            `/${
              admin ? "admin/" : client ? "" : "representante/"
            }confirmacao-pedidos/${e.orderId}`
          )
        }
      />
      <Pagination
        totalCountOfRegisters={data?.totalCount ?? 0}
        currentPage={page}
        onPageChange={setPage}
      />
    </PanelLayout>
  );
};

export default PanelOrderConfirm;
